/**
 * Services Hook
 * Custom hook for managing services data and operations
 * Aligns with backend inventory.services module
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../../contexts/AuthContext';
import toast from 'react-hot-toast';

export const useServices = (options = {}) => {
  const { autoFetch = true } = options;
  
  const { token } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch services
  const fetchServices = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Ensure authorization header is set
      if (token && !axios.defaults.headers.common['Authorization']) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      // Using backend modular route structure
      const response = await axios.get('/api/inventory/services');
      const services = (response.data || []).map(service => {
        // Parse paymentSettings and map to paymentMethods for frontend compatibility
        const paymentSettings = typeof service.paymentSettings === 'string' ? JSON.parse(service.paymentSettings) : (service.paymentSettings || {});
        
        return {
          ...service,
          // Parse JSON fields back into objects/arrays
          availability: typeof service.availability === 'string' ? JSON.parse(service.availability) : (service.availability || []),
          paymentSettings: paymentSettings,
          paymentMethods: paymentSettings, // Map paymentSettings to paymentMethods for frontend compatibility
          hosts: typeof service.hosts === 'string' ? JSON.parse(service.hosts) : (service.hosts || []),
          rsvpSettings: typeof service.rsvpSettings === 'string' ? JSON.parse(service.rsvpSettings) : (service.rsvpSettings || {}),
          questionnaire: typeof service.questionnaire === 'string' ? JSON.parse(service.questionnaire) : (service.questionnaire || {}),
          privacySettings: typeof service.privacySettings === 'string' ? JSON.parse(service.privacySettings) : (service.privacySettings || {}),
          reminderSettings: typeof service.reminderSettings === 'string' ? JSON.parse(service.reminderSettings) : (service.reminderSettings || {}),
          pricingTiers: typeof service.pricingTiers === 'string' ? JSON.parse(service.pricingTiers) : (service.pricingTiers || []),
          enableAddons: typeof service.enableAddons === 'string' ? JSON.parse(service.enableAddons) : (service.enableAddons || false),
          addons: typeof service.addons === 'string' ? JSON.parse(service.addons) : (service.addons || []),
          // Enhanced session management fields
          enableSessions: service.enableSessions || false,
          sessions: typeof service.sessions === 'string' ? JSON.parse(service.sessions) : (service.sessions || []),
          discountSettings: typeof service.discountSettings === 'string' ? JSON.parse(service.discountSettings) : (service.discountSettings || {})
        };
      });
      setServices(services);
    } catch (err) {
      console.error('Error fetching services:', err);
      setError(err.message);
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Create new service
  const createService = useCallback(async (serviceData) => {
    console.log('🔍 useServices createService - Raw serviceData:', serviceData);
    console.log('🔍 useServices createService - enableSessions:', serviceData.enableSessions);
    console.log('🔍 useServices createService - sessions:', serviceData.sessions);

    try {
      // Clean the data to prevent circular reference errors
      const cleanData = {
        name: serviceData.name,
        description: serviceData.description,
        rate: parseFloat(serviceData.rate) || 0,
        rateType: serviceData.rateType?.toUpperCase() || 'HOURLY',
        // Only use basic availability when Advanced Form is disabled
        availability: serviceData.enableSessions
          ? JSON.stringify([]) // Clear basic availability when using advanced sessions
          : JSON.stringify(serviceData.availability || []),
        location: serviceData.location || null,
        photo: serviceData.photo || null,
        photoUrls: serviceData.photoUrls || null,
        photoBaseKey: serviceData.photoBaseKey || null,
        showPaymentOptions: serviceData.showPaymentOptions || false,
        notes: serviceData.notes || null,
        // Advanced settings (stored as JSON)
        paymentSettings: JSON.stringify(serviceData.paymentMethods || {}),
        paymentNote: serviceData.paymentNote || null,
        hosts: JSON.stringify(serviceData.hosts || []),
        rsvpSettings: JSON.stringify(serviceData.rsvpSettings || {}),
        questionnaire: JSON.stringify(serviceData.questionnaire || {}),
        privacySettings: JSON.stringify(serviceData.privacySettings || {}),
        reminderSettings: JSON.stringify(serviceData.reminderSettings || {}),
        // Tiered pricing
        enableTieredPricing: serviceData.enableTieredPricing !== undefined ? serviceData.enableTieredPricing : false,
        pricingTiers: JSON.stringify(serviceData.pricingTiers || []),
        // Add-ons
        enableAddons: serviceData.enableAddons || false,
        addons: JSON.stringify(serviceData.addons || []),
        // Enhanced session management
        enableSessions: serviceData.enableSessions || false,
        sessions: JSON.stringify(serviceData.sessions || []),
        discountSettings: JSON.stringify(serviceData.discountSettings || {}),
        // Bundle discount
        bundleDiscount: serviceData.bundleDiscount ? parseFloat(serviceData.bundleDiscount) : null
      };

      console.log('🔍 useServices createService - cleanData being sent:', cleanData);
      console.log('🔍 useServices createService - cleanData.enableSessions:', cleanData.enableSessions);
      console.log('🔍 useServices createService - cleanData.sessions:', cleanData.sessions);
      console.log('🔍 useServices createService - availability vs sessions conflict resolved:', {
        enableSessions: cleanData.enableSessions,
        availabilityCleared: cleanData.enableSessions ? 'YES (empty array)' : 'NO (using basic slots)',
        sessionsUsed: cleanData.enableSessions ? 'YES' : 'NO'
      });

      const response = await axios.post('/api/inventory/services', cleanData);
      toast.success('Service created successfully');
      await fetchServices(); // Refresh list
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to create service';
      toast.error(errorMessage);
      throw err;
    }
  }, [fetchServices]);

  // Update existing service
  const updateService = useCallback(async (serviceId, serviceData) => {
    console.log('🔍 useServices updateService - Raw serviceData:', serviceData);
    console.log('🔍 useServices updateService - enableSessions:', serviceData.enableSessions);
    console.log('🔍 useServices updateService - sessions:', serviceData.sessions);

    // DEBUG: Check photo fields with safer type checking
    console.log('📸 [updateService] Photo fields being sent:', {
      photo: (() => {
        try {
          if (!serviceData.photo) return 'null';
          if (typeof serviceData.photo === 'string') {
            return `${serviceData.photo.substring(0, 100)}... (${serviceData.photo.length} chars)`;
          } else {
            return `Object: ${JSON.stringify(serviceData.photo)}`;
          }
        } catch (e) {
          return `Error processing photo: ${e.message}`;
        }
      })(),
      photoUrls: (() => {
        try {
          if (!serviceData.photoUrls) return 'null';
          if (typeof serviceData.photoUrls === 'string') {
            return `${serviceData.photoUrls.substring(0, 100)}... (${serviceData.photoUrls.length} chars)`;
          } else {
            return `Object: ${JSON.stringify(serviceData.photoUrls)}`;
          }
        } catch (e) {
          return `Error processing photoUrls: ${e.message}`;
        }
      })(),
      photoBaseKey: serviceData.photoBaseKey,
      photoType: typeof serviceData.photo,
      photoUrlsType: typeof serviceData.photoUrls,
      isPhotoBase64: serviceData.photo?.startsWith?.('data:'),
      isPhotoUrlsBase64: serviceData.photoUrls?.startsWith?.('data:')
    });

    try {
      // Clean the data to prevent circular reference errors
      const cleanData = {
        name: serviceData.name,
        description: serviceData.description,
        rate: parseFloat(serviceData.rate) || 0,
        rateType: serviceData.rateType?.toUpperCase() || 'HOURLY',
        // Only use basic availability when Advanced Form is disabled
        availability: serviceData.enableSessions
          ? JSON.stringify([]) // Clear basic availability when using advanced sessions
          : JSON.stringify(serviceData.availability || []),
        location: serviceData.location || null,
        photo: serviceData.photo || null,
        photoUrls: serviceData.photoUrls || null,
        photoBaseKey: serviceData.photoBaseKey || null,
        showPaymentOptions: serviceData.showPaymentOptions || false,
        notes: serviceData.notes || null,
        // Advanced settings (stored as JSON)
        paymentSettings: JSON.stringify(serviceData.paymentMethods || {}),
        paymentNote: serviceData.paymentNote || null,
        hosts: JSON.stringify(serviceData.hosts || []),
        rsvpSettings: JSON.stringify(serviceData.rsvpSettings || {}),
        questionnaire: JSON.stringify(serviceData.questionnaire || {}),
        privacySettings: JSON.stringify(serviceData.privacySettings || {}),
        reminderSettings: JSON.stringify(serviceData.reminderSettings || {}),
        // Tiered pricing
        enableTieredPricing: serviceData.enableTieredPricing !== undefined ? serviceData.enableTieredPricing : false,
        pricingTiers: JSON.stringify(serviceData.pricingTiers || []),
        // Add-ons
        enableAddons: serviceData.enableAddons || false,
        addons: JSON.stringify(serviceData.addons || []),
        // Enhanced session management
        enableSessions: serviceData.enableSessions || false,
        sessions: JSON.stringify(serviceData.sessions || []),
        discountSettings: JSON.stringify(serviceData.discountSettings || {}),
        // Bundle discount
        bundleDiscount: serviceData.bundleDiscount ? parseFloat(serviceData.bundleDiscount) : null
      };

      console.log('🔍 useServices updateService - cleanData being sent:', cleanData);
      console.log('🔍 useServices updateService - cleanData.enableSessions:', cleanData.enableSessions);
      console.log('🔍 useServices updateService - cleanData.sessions:', cleanData.sessions);
      console.log('🔍 useServices updateService - availability vs sessions conflict resolved:', {
        enableSessions: cleanData.enableSessions,
        availabilityCleared: cleanData.enableSessions ? 'YES (empty array)' : 'NO (using basic slots)',
        sessionsUsed: cleanData.enableSessions ? 'YES' : 'NO'
      });

      const response = await axios.put(`/api/inventory/services/${serviceId}`, cleanData);
      toast.success('Service updated successfully');
      await fetchServices(); // Refresh list
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to update service';
      toast.error(errorMessage);
      throw err;
    }
  }, [fetchServices]);

  // Delete service
  const deleteService = useCallback(async (serviceId) => {
    try {
      await axios.delete(`/api/inventory/services/${serviceId}`);
      toast.success('Service deleted successfully');
      await fetchServices(); // Refresh list
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to delete service';
      toast.error(errorMessage);
      throw err;
    }
  }, [fetchServices]);

  // Generate share link for service
  const generateShareLink = useCallback(async (serviceId) => {
    try {
      const response = await axios.post(`/api/inventory/services/${serviceId}/generate-share-link`);
      toast.success('Share link generated successfully');
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to generate share link';
      toast.error(errorMessage);
      throw err;
    }
  }, []);

  // Delete share link for service
  const deleteShareLink = useCallback(async (serviceId) => {
    try {
      await axios.delete(`/api/inventory/services/${serviceId}/share-link`);
      toast.success('Share link removed');
      await fetchServices(); // Refresh to update share link status
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to delete share link';
      toast.error(errorMessage);
      throw err;
    }
  }, [fetchServices]);

  // Toggle service availability
  const toggleAvailability = useCallback(async (serviceId) => {
    try {
      const service = services.find(s => s.id === serviceId);
      if (!service) throw new Error('Service not found');
      
      const response = await axios.patch(`/api/inventory/services/${serviceId}`, {
        isAvailable: !service.isAvailable
      });
      toast.success(`Service ${response.data.isAvailable ? 'enabled' : 'disabled'}`);
      await fetchServices(); // Refresh list
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to toggle availability';
      toast.error(errorMessage);
      throw err;
    }
  }, [services, fetchServices]);

  // Get service by ID
  const getServiceById = useCallback((serviceId) => {
    return services.find(service => service.id === serviceId);
  }, [services]);

  // Filter services
  const filterServices = useCallback((filters = {}) => {
    let filteredServices = [...services];
    
    if (filters.isAvailable !== undefined) {
      filteredServices = filteredServices.filter(service => 
        service.isAvailable === filters.isAvailable
      );
    }
    
    if (filters.rateType) {
      filteredServices = filteredServices.filter(service => 
        service.rateType === filters.rateType
      );
    }
    
    if (filters.searchTerm) {
      const search = filters.searchTerm.toLowerCase();
      filteredServices = filteredServices.filter(service =>
        service.name?.toLowerCase().includes(search) ||
        service.description?.toLowerCase().includes(search)
      );
    }
    
    return filteredServices;
  }, [services]);

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch && token) {
      fetchServices();
    }
  }, [autoFetch, token]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    services,
    loading,
    error,
    fetchServices,
    createService,
    updateService,
    deleteService,
    generateShareLink,
    deleteShareLink,
    toggleAvailability,
    getServiceById,
    filterServices,
    // Computed values
    totalServices: services.length,
    activeServices: services.filter(s => s.isAvailable).length,
    servicesWithShareLinks: services.filter(s => s.shareToken).length
  };
};